import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import ar from './ar.json';
import en from './en.json';

export type Locale = 'ar' | 'en';
type Dict = typeof ar;
// English has two plural forms where Arabic has six, so the two dictionaries are not the same shape.
const dicts: Record<Locale, Dict> = { ar, en: en as unknown as Dict };
const STORAGE_KEY = 'wasity.locale';

function lookup(dict: Dict, key: string): unknown {
  return key.split('.').reduce<unknown>((o, k) => (o && typeof o === 'object' ? (o as Record<string, unknown>)[k] : undefined), dict);
}

/** The variable a count sentence agrees with, when the entry does not name one in `_by`. */
const COUNT_VARS = ['n', 'count', 'seconds'] as const;
const pluralRules: Record<Locale, Intl.PluralRules> = { ar: new Intl.PluralRules('ar'), en: new Intl.PluralRules('en') };
const PLURAL_KEYS = new Set(['zero', 'one', 'two', 'few', 'many', 'other', '_by']);
/** Only the plural forms and nothing else: a namespace that merely has an `other` entry (a list of reasons, say) is not one. */
export const isPluralEntry = (v: unknown): v is Record<string, string> =>
  !!v && typeof v === 'object' && 'other' in v && Object.keys(v).every((k) => PLURAL_KEYS.has(k));

/**
 * A dictionary entry is a sentence, or — for a sentence about a number — one sentence per plural form:
 * `{ zero, one, two, few, many, other }` in Arabic, `{ one, other }` in English. Arabic has six forms
 * and «1 خانات», «2 دقيقة», «11 طلبات» were what one sentence for all of them produced. A missing form
 * falls back to `other`.
 */
export function resolveEntry(entry: unknown, locale: Locale, vars?: Record<string, string | number>): string | undefined {
  if (typeof entry === 'string') return entry;
  if (!isPluralEntry(entry)) return undefined;
  const forms = entry;
  const by = forms._by ?? COUNT_VARS.find((k) => vars?.[k] !== undefined);
  const n = Number(by ? vars?.[by] : NaN);
  return Number.isFinite(n) ? (forms[pluralRules[locale].select(n)] ?? forms.other) : forms.other;
}

interface I18n {
  locale: Locale;
  dir: 'rtl' | 'ltr';
  t: (key: string, vars?: Record<string, string | number>) => string;
  setLocale: (l: Locale) => void;
  /** Status labels and other AR/EN pairs coming from the shared package. */
  pick: <T extends { ar: string; en: string }>(v: T) => string;
  /**
   * A request's title in the reader's language. The API sends `title` (Arabic) and `titleEn` beside
   * it; an English reader was getting the Arabic one on every list and detail page.
   */
  localTitle: (r: { title?: string | null; titleEn?: string | null } | null | undefined) => string;
}

const Ctx = createContext<I18n | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved === 'en' ? 'en' : 'ar';
    } catch {
      return 'ar';
    }
  });
  const dir = locale === 'ar' ? 'rtl' : 'ltr';
  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = dir;
  }, [locale, dir]);
  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    try {
      localStorage.setItem(STORAGE_KEY, l);
    } catch {
      /* private mode */
    }
  }, []);
  const t = useCallback(
    (key: string, vars?: Record<string, string | number>) => {
      const raw = resolveEntry(lookup(dicts[locale], key), locale, vars) ?? resolveEntry(lookup(dicts.ar, key), 'ar', vars) ?? key;
      return vars ? raw.replace(/\{(\w+)\}/g, (_, k: string) => String(vars[k] ?? '')) : raw;
    },
    [locale],
  );
  const value = useMemo<I18n>(
    () => ({ locale, dir, t, setLocale, pick: (v) => v[locale], localTitle: (r) => (locale === 'en' && r?.titleEn ? r.titleEn : (r?.title ?? '')) }),
    [locale, dir, t, setLocale],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useI18n(): I18n {
  const v = useContext(Ctx);
  if (!v) throw new Error('useI18n outside I18nProvider');
  return v;
}

/** Western digits, Riyadh time, short. */
export function formatDateTime(d: string | Date, locale: Locale) {
  return new Intl.DateTimeFormat(locale === 'ar' ? 'ar-SA-u-nu-latn-ca-gregory' : 'en-GB', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Riyadh' }).format(new Date(d));
}
export function formatRelative(d: string | Date, locale: Locale) {
  const diff = (new Date(d).getTime() - Date.now()) / 1000;
  const rtf = new Intl.RelativeTimeFormat(locale === 'ar' ? 'ar' : 'en', { numeric: 'auto' });
  const abs = Math.abs(diff);
  if (abs < 60) return rtf.format(Math.round(diff), 'second');
  if (abs < 3600) return rtf.format(Math.round(diff / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(diff / 3600), 'hour');
  return rtf.format(Math.round(diff / 86400), 'day');
}
